import * as React from 'react';
import './editModalAutoComplete.scss';
import { ILabEmployee } from 'typings/employeeErrorData';
import { AutoComplete } from 'components/Common/autoComplete';
import { defaultSearchResultLimit } from 'common/constants';
import { IEmployeeIdMap, ISuggestedEmployee } from 'typings/employee';

interface EditModalManagerAutoCompleteProps {
    employeeIds: IEmployeeIdMap;
    labEmployee: Nullable<ILabEmployee>;
    isSending: boolean;
    errorDetails: { [id: string]: string };
    isEmptyManagerIdValid:  (labEmployee: Nullable<ILabEmployee>) => boolean;
    onUpdateCallBack: (labEmployee: Nullable<ILabEmployee>, errorDetails: { [id: string]: string }) => void
}

export default class EditModalManagerAutoComplete extends React.Component<EditModalManagerAutoCompleteProps> {
    constructor(props: EditModalManagerAutoCompleteProps) {
        super(props);
        this.autocompleteSearchManagerId = this.autocompleteSearchManagerId.bind(this);
        this.handleRemoveManagerId = this.handleRemoveManagerId.bind(this);
        this.handleSelectManagerIdChange = this.handleSelectManagerIdChange.bind(this);
        this.renderWarning = this.renderWarning.bind(this);

        this.state = {
            employeeIds: {}
        };
    }

    render(): React.ReactNode {
        const { labEmployee, errorDetails } = this.props;
        const managerIdValue = errorDetails.managerNumber ? '' : (labEmployee?.managerNumber ?? '');
        const error = errorDetails.managerNumber || (this.props.isEmptyManagerIdValid(labEmployee) ? '' : 'Invalid manager number');

        return (
            <div id="managerId" className="nomst-edit-modal-autocomplete">
                <label htmlFor="searchManager" className="nomst-edit-modal-autocomplete__field-label">Manager ID</label>
                <AutoComplete
                    id="searchManager"
                    placeholder={'Search for a Manager'}
                    search={this.autocompleteSearchManagerId}
                    resultLimit={defaultSearchResultLimit}
                    onChangeInput={this.handleRemoveManagerId}
                    onSuggestionSelected={this.handleSelectManagerIdChange}
                    onClearSearch={this.handleRemoveManagerId}
                    getSelectedValue={this.formatEmployee}
                    getRenderedSuggestionText={this.formatEmployee}
                    initialValue={this.formatEmployee(this.props.employeeIds[managerIdValue])}
                    disableInput={this.props.isSending}
                />
                {error && this.renderWarning(error, 'Select Manager')}
            </div>
        );
    }

    private async autocompleteSearchManagerId(searchText: string, resultLimit: number): Promise<ISuggestedEmployee[]> {
        if (!searchText?.trim().length) {
            return [];
        }
        const { employeeIds } = this.props;
        const results: ISuggestedEmployee[] = [];
        const search = searchText.toLowerCase();

        Object.keys(employeeIds).forEach(key => {
            const e = employeeIds[key];
            if (key.toLowerCase().includes(search) || e.displayName.toLowerCase().includes(search)){
                results.push(e);
            }
        });

        return results.slice(0, resultLimit);
    }

    private formatEmployee(e?: ISuggestedEmployee): string {
        if (!e) {
            return '';
        }
        return `${e.displayName} (${e.employeeSourceId})`;
    }

    private handleRemoveManagerId(): void {
        const { labEmployee, errorDetails } = { ...this.props };
        if (labEmployee) {
            labEmployee.managerNumber = '';
        }
        errorDetails.managerNumber = 'Missing';
        this.props.onUpdateCallBack(labEmployee, errorDetails);
    }

    private handleSelectManagerIdChange(item: ISuggestedEmployee): void {
        const { labEmployee } = { ...this.props };
        if(labEmployee && item?.employeeSourceId.length && item.employeeSourceId !== labEmployee.employeeNumber) {
            labEmployee.managerNumber = item.employeeSourceId;
            const { errorDetails } = {...this.props};
            delete errorDetails['managerNumber'];
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private renderWarning(
        error?: Nullable<string>,
        errorMessage?: Nullable<string>): JSX.Element {
        const errorToDisplay = error ? errorMessage : '';

        return (
            <label htmlFor="searchManager" className="nomst-edit-modal-autocomplete__field-warning">{errorToDisplay}</label>
        );
    }
}